/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 14:22:11
 **/

import { useState, useEffect } from "react";

import { Button, Column } from "@/frameworks/designStuff/components";
import { SidebarID } from "@/frameworks/core/commons";

import { usePhoneCall, usePeopleManagerHandler, useSidebarActions } from "@/frameworks/core/hooks";
import { SwitchCase } from "@/frameworks/libraries";

import * as S from "../emotion";
import UserProfile from "./UserProfile";
import MediaLayout from "./MediaLayout";
import CommandBar from "./CommandBar";
import NavigateBar from "./NavigateBar";
import CallHistoryTabs from "./CallHistoryTabs";
import { useCallManager } from "./hooks";
import { hasCallGatewayClient } from "@/modules/phone/utils";
import PhoneNumberProfile from "./UserProfile/PhoneNumberProfile";
import SingleCallMediaLayout from "./MediaLayout/SingleCallMediaLayout";

// const LOGTAG = "#PhoneDetail#";

export default function PhoneDetail(props: PushServices.Call.PhoneDetailProps) {
	const sidebarActions = useSidebarActions();
	const { check_for_floating, new_props } = useCallManager(props);
	const phoneCall = usePhoneCall();
	const { updatePeople } = usePeopleManagerHandler();

	const [isBlockedByMe, setIsBlockedByMe] = useState(false);
	const callees = phoneCall.state.callees[0];
	const participatedUserIDList = phoneCall.state.participatedUserList(props.callID || "");
	const isGateWayCallMember = callees.some((callee) =>
		hasCallGatewayClient(callee as SwaggerModels.CallUserResponseVO)
	);

	const handleCloseClick = () => {
		check_for_floating();
		sidebarActions.hide(SidebarID.PhoneDetail);
	};

	useEffect(() => {
		const blockedByMe =
			callees
				.filter((callee) => participatedUserIDList.includes(callee?.userID))
				.map((callee) => callee.blockedByMe)[0] || false;
		setIsBlockedByMe(blockedByMe);
	}, [callees]);

	const handleUnBlock = async () => {
		try {
			await updatePeople({
				type: "block",
				payload: {
					active: !callees[0].blockedByMe,
					userID: callees[0].userId || ""
				}
			});
			setIsBlockedByMe(false);
		} catch (error) {
			console.warn("#PhoneDetail# error", error);
		}
	};

	return (
		<S.Wrap>
			<S.HeaderIconList horizonAlign="left">
				<Button.Icon name="Left24" size="large" variant="text" onClick={handleCloseClick} />
			</S.HeaderIconList>
			<S.Body>
				<SwitchCase
					condition={isGateWayCallMember ? "gateway" : "default"}
					cases={{
						default: (
							<>
								<Column gap={12} style={{ width: "100%", marginBottom: "24px" }} horizonAlign="center">
									<Column.div horizonAlign="center" gap={20} style={{ width: "100%" }}>
										<UserProfile {...new_props} isBlockedByMe={isBlockedByMe} />
										<MediaLayout {...new_props} />
									</Column.div>
									<CommandBar
										{...new_props}
										visibleSubCommand={true}
										onUnBlock={handleUnBlock}
										isBlockedByMe={isBlockedByMe}
									/>
								</Column>
								<NavigateBar {...new_props} />
								<CallHistoryTabs {...new_props} />
							</>
						),
						gateway: (
							<>
								<Column gap={12} style={{ width: "100%", marginBottom: "24px" }} horizonAlign="center">
									<Column.div horizonAlign="center" gap={20} style={{ width: "100%" }}>
										<PhoneNumberProfile {...new_props} isBlockedByMe={isBlockedByMe} />
										<SingleCallMediaLayout
											callees={participatedUserIDList}
											layout={"AVATARONLY"}
											callID={phoneCall.state.id[0]}
										/>
									</Column.div>
									<CommandBar
										{...new_props}
										visibleSubCommand={false}
										onUnBlock={handleUnBlock}
										isBlockedByMe={isBlockedByMe}
									/>
								</Column>
								<CallHistoryTabs {...new_props} />
							</>
						)
					}}
				/>
			</S.Body>
		</S.Wrap>
	);
}
